<template>
    <div class="mt-1">
        <card plain v-for="i in patient.programEligibilities" :key="i.id"
            :class="{ 'mb-2': true, 'cursor-pointer': canManage }" @click.native="edit(i)">
            <div class="d-flex align-items-center">
                <div class="flex-fill">
                    <div>
                        <strong>{{ programName(i) }}</strong>
                    </div>
                    <small class="d-flex" style="gap: 8px;">
                        <span v-if="i.reason">{{ i.reason }}</span>
                        <span v-if="i.approvalExpirationDate">
                            Expires: {{ i.approvalExpirationDate | formatDate("LL", false) }}
                        </span>
                    </small>
                </div>
                <b-badge :variant="i.eligible ? 'success' : 'danger'" class="m-0">
                    {{ i.eligible ? 'Eligible' : 'Not Eligible' }}
                </b-badge>
            </div>
        </card>

        <b-button v-if="canManage" block variant="success" @click="createNew">
            <i class="fas fa-plus" />
            Add Eligible Program
        </b-button>

        <b-modal :visible="!!selectedProgramEligibility" title="Eligible Program"
            @hidden="selectedProgramEligibility = null" ok-title="Save" no-fade @ok="save">
            <template v-if="selectedProgramEligibility">
                <search-component v-model="selectedProgram" search-u-r-l="/Program" prepopulate label="Program" :disabled="!!selectedProgramEligibility.id" />

                <b-check v-model="selectedProgramEligibility.eligible" switch class="mb-1">Eligible</b-check>

                <b-form-group label="Reason">
                    <b-input v-model="selectedProgramEligibility.reason" maxlength="50" />
                </b-form-group>

                <b-form-group label="Approval Expiration Date">
                    <el-date-picker v-model="selectedProgramEligibility.approvalExpirationDate" format="MM/dd/yyyy"
                        type="date" today-button reset-button />
                </b-form-group>

                <template v-if="selectedProgramEligibility.created || selectedProgramEligibility.updated">
                    <hr>
                    <b-row class="history">
                        <b-col v-if="selectedProgramEligibility.created">
                            <h6>Created</h6>
                            {{ selectedProgramEligibility.created | formatDate("LLL", true) }}<br>
                            <small>{{ selectedProgramEligibility.createdBy }}</small>
                        </b-col>
                        <b-col v-if="selectedProgramEligibility.updated">
                            <h6>Updated</h6>
                            {{ selectedProgramEligibility.updated | formatDate("LLL", true) }}<br>
                            <small>{{ selectedProgramEligibility.updatedBy }}</small>
                        </b-col>
                    </b-row>
                </template>
            </template>
            <template #modal-footer>
                <div v-if="isSaving" class="text-center">
                    <b-spinner small /> Saving...
                </div>
            </template>
        </b-modal>
    </div>
</template>

<script lang="ts">
import { Component, VModel, Vue } from "vue-property-decorator";
import { Patient, PatientProgramEligibility } from "@/models/Patient";
import Checkbox from "@/components/Inputs/Checkbox.vue";
import HelpPopup from "../HelpPopup.vue";
import SearchComponent from "@/components/SearchComponent.vue";
import { Program } from "@/models/Program";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { DatePicker } from "element-ui";
import { BvModalEvent } from "bootstrap-vue";
import { toRaw } from "vue";

@Component({
    name: "PatientProgramEligibilityComponent",
    components: { SearchComponent, Checkbox, HelpPopup, [DatePicker.name]: DatePicker, },
})
export default class PatientProgramEligibilityComponent extends Vue {
    @VModel() private patient!: Patient;

    private selectedProgramEligibility: PatientProgramEligibility | null = null;
    private selectedProgram = new Program();

    protected programs: Program[] = [];
    protected isSaving = false;

    mounted() {
        this.fetchPrograms();
    }

    get canManage() {
        return this.$permissions.some(p => p.permissionCodeName === "Patient Dashboard/Manage Eligible Programs");
    }

    programName(prog: PatientProgramEligibility) {
        const program = this.programs.find((p) => p.id == prog.programId);
        return program?.name;
    }

    async fetchPrograms() {
        try {
            const resp = await this.$http.get<Program[]>('/Program');
            this.programs = resp.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    createNew() {
        if (!this.canManage) return;
        this.selectedProgramEligibility = { id: 0, programId: 0, patientId: this.patient.id, eligible: true };
    }

    edit(item: PatientProgramEligibility) {
        if (!this.canManage) return;
        this.selectedProgramEligibility = structuredClone(toRaw(item));
        this.selectedProgram = Object.assign(new Program(), { id: item.programId });
    }

    async save(event: BvModalEvent) {
        event.preventDefault();

        const programEligibilities = [
            ...this.patient.programEligibilities.filter(pe => pe.id !== this.selectedProgramEligibility!.id)
        ];
        programEligibilities.push({
            ...this.selectedProgramEligibility,
            programId: this.selectedProgram.id
        } as PatientProgramEligibility
        );

        try {
            this.isSaving = true;
            const resp = await this.$http.post<{ patient: Patient, warning: string }>('/api/Patient', {
                ...this.patient,
                programEligibilities
            });
            const pt = new Patient(resp.data.patient);
            this.patient.programEligibilities = pt.programEligibilities;
            this.selectedProgramEligibility = null;
            this.selectedProgram = new Program();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isSaving = false;
        }
    }
}
</script>

<style scoped lang="scss">
.history {
    font-size: .9em;
    line-height: 1.5;
}
</style>
